import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { invoiceService, Invoice } from "../services/invoice.service";
import { formatLocalDate } from "../utils/dateUtils";
import CreateInvoiceModal from "../components/modals/CreateInvoiceModal";

const InvoiceListPage: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await invoiceService.getInvoices();
      setInvoices(data.invoices);
    } catch (error) {
      console.error("Failed to load invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (id: number) => {
    navigate(`/invoices/${id}/edit`);
  };

  const handleCreateSuccess = (invoiceId: number) => {
    setCreateModalOpen(false);
    navigate(`/invoices/${invoiceId}/edit`);
  };

  const getStatusColor = (
    statusId: number,
  ): "default" | "primary" | "success" | "warning" | "error" => {
    switch (statusId) {
      case 1:
        return "default"; // Building
      case 2:
        return "warning"; // Invoiced
      case 3:
        return "success"; // Paid
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='400px'
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={3}
        >
          <Typography variant='h4'>Invoices</Typography>
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Invoice
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Invoice Date</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Comment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Typography variant='body2' color='textSecondary'>
                      No invoices found. Click "Create Invoice" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    hover
                    onClick={() => handleRowClick(invoice.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>{invoice.clientName}</TableCell>
                    <TableCell>
                      {formatLocalDate(invoice.invoiceDate)}
                    </TableCell>
                    <TableCell>${invoice.invoiceTotal.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.statusName || "Unknown"}
                        color={getStatusColor(invoice.statusId)}
                        size='small'
                      />
                    </TableCell>
                    <TableCell>{invoice.comment || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateInvoiceModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      </Box>
    </Layout>
  );
};

export default InvoiceListPage;
